import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { servicesAPI } from '../../services/api';

export interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  price?: number;
  currency?: string;
  category?: string;
  isActive: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface ServicesState {
  requestId?: string;
  services: Service[];
  loading: boolean;
  error: string | null;
}

const initialState: ServicesState = {
  services: [],
  loading: false,
  error: null,
};

export const fetchServices = createAsyncThunk(
  'services/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await servicesAPI.getAll();
      return response.data.services;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la încărcarea serviciilor');
    }
  }
);

export const createService = createAsyncThunk(
  'services/createService',
  async (serviceData: Partial<Service>, { rejectWithValue }) => {
    try {
      const response = await servicesAPI.create(serviceData);
      return response.data.service;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la crearea serviciului');
    }
  }
);

export const updateService = createAsyncThunk(
  'services/updateService',
  async ({ id, data }: { id: string; data: Partial<Service> }, { rejectWithValue }) => {
    try {
      const response = await servicesAPI.update(id, data);
      return response.data.service;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la actualizarea serviciului');
    }
  }
);

export const deleteService = createAsyncThunk(
  'services/deleteService',
  async (id: string, { rejectWithValue }) => {
    try {
      await servicesAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la ștergerea serviciului');
    }
  }
);

const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    setBookingServices: (state, action) => { state.services = action.payload; },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.services = action.payload;
        state.error = null;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.services.push(action.payload);
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const index = state.services.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.services[index] = action.payload;
        }
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.services = state.services.filter(s => s.id !== action.payload);
      });
  },
});

export const { clearError, setBookingServices } = servicesSlice.actions;
export default servicesSlice.reducer;
